'use client';

import * as LucideIcons from 'lucide-react';
import type { ProductFeature } from '@/lib/data/software';

interface FeatureGridProps {
  features: ProductFeature[];
  className?: string;
}

// Get a Lucide icon by name, with fallback
function getIcon(iconName: string | null): React.ElementType {
  if (!iconName) return LucideIcons.CheckCircle;

  // Convert kebab-case or snake_case to PascalCase
  const pascalName = iconName
    .split(/[-_]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IconComponent = (LucideIcons as unknown as Record<string, React.ElementType>)[pascalName];
  return IconComponent || LucideIcons.CheckCircle;
}

export default function FeatureGrid({ features, className = '' }: FeatureGridProps) {
  if (!features || features.length === 0) {
    return null;
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 ${className}`}>
        {features.map((feature) => {
          const Icon = getIcon(feature.icon);
          return (
            <div
              key={feature.id}
              className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 sm:p-5 hover:bg-gray-100 dark:hover:bg-gray-800 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-sm">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}
